import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/ngos/auth/callback')({
	validateSearch: v.object({
		token: v.fallback(v.string(), ''),
		redirect: v.fallback(v.optional(v.string()), undefined),
	}),
	component: AuthCallbackPage,
});

function AuthCallbackPage() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { token, redirect } = Route.useSearch();
	const hasValidated = useRef(false);
	const [error, setError] = useState<'expired' | 'invalid' | null>(null);

	const { mutate: validateToken } = useMutation({
		...trpc.auth.validateMagicLink.mutationOptions(),
		onSuccess: (result) => {
			if (result.success && result.requiresSelection) {
				navigate({ to: '/ngos/select-ngo', search: { redirect } });
			} else if (result.success) {
				queryClient.removeQueries({ queryKey: trpc.ngo.pathKey() });
				if (redirect) {
					window.location.href = redirect;
				} else {
					navigate({ to: '/ngos/projects' });
				}
			} else {
				setError(result.error === 'expired' ? 'expired' : 'invalid');
			}
		},
		onError: () => {
			setError('invalid');
		},
	});

	useEffect(() => {
		if (token && !hasValidated.current) {
			hasValidated.current = true;
			validateToken({ token });
		}
	}, [token, validateToken]);

	const errorMessage =
		error === 'expired'
			? t`This login link has expired. Please request a new one.`
			: error
				? t`This login link is invalid. Please request a new one.`
				: null;

	return (
		<div className="flex min-h-screen items-center justify-center bg-white">
			<div className="flex flex-col items-center gap-6 p-8">
				<WorldcooLogo accentClassName="text-[#f28e41]" className="h-[60px] w-auto" />

				{!error && (
					<div className="flex flex-col items-center gap-3">
						<div className="size-8 animate-spin rounded-full border-4 border-[#e5e7eb] border-t-[#f28e41]" />
						<p className="text-base text-[#6b7280]">{t`Verifying your login link...`}</p>
					</div>
				)}

				{error && (
					<div className="flex flex-col items-center gap-4">
						<div className="flex size-12 items-center justify-center rounded-full bg-red-100">
							<svg
								className="size-6 text-red-600"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</div>
						<p className="text-center text-base text-[#6b7280]">{errorMessage}</p>
						<button
							type="button"
							onClick={() => navigate({ to: '/ngos/login' })}
							className="mt-2 text-sm font-medium text-[#f28e41] underline hover:text-[#d45a00]"
						>
							{t`Back to login`}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
